import { useEffect, useRef } from 'react';

interface FireworksStreamersOverlayProps {
  trigger: number;
}

type RgbColor = readonly [number, number, number];

interface Rocket {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  targetY: number;
  color: RgbColor;
  trail: Array<{ x: number; y: number }>;
}

interface Spark {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  alpha: number;
  decay: number;
  size: number;
  color: RgbColor;
}

interface Streamer {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  length: number;
  angle: number;
  angularVelocity: number;
  alpha: number;
  color: RgbColor;
}

const CELEBRATION_COLORS: readonly RgbColor[] = [
  [249, 115, 22],
  [236, 72, 153],
  [34, 211, 238],
  [168, 85, 247],
  [250, 204, 21],
  [74, 222, 128],
];

const CELEBRATION_DURATION_MS = 4_500;

function colorString(color: RgbColor, alpha = 1): string {
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

function randomColor(): RgbColor {
  return CELEBRATION_COLORS[Math.floor(Math.random() * CELEBRATION_COLORS.length)]!;
}

function playCelebrationSound(): () => void {
  let context: AudioContext | null = null;
  let closeTimer: number | null = null;

  const close = () => {
    if (closeTimer !== null) window.clearTimeout(closeTimer);
    if (context && context.state !== 'closed') {
      void context.close().catch(() => undefined);
    }
  };

  try {
    const AudioContextConstructor = window.AudioContext
      ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return close;

    const audioContext = new AudioContextConstructor();
    context = audioContext;
    const now = audioContext.currentTime;
    const notes = [523.25, 659.25, 783.99];

    notes.forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const start = now + index * 0.12;
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.24);
      oscillator.connect(gain).connect(audioContext.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.26);
    });

    closeTimer = window.setTimeout(close, 700);
  } catch {
    // Audio feedback is optional and may be blocked by browser autoplay controls.
  }

  return close;
}

export function FireworksStreamersOverlay({ trigger }: FireworksStreamersOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (trigger === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let frameId: number | null = null;
    let startTime = performance.now();
    let lastTime = startTime;
    let launchIndex = 0;
    const rockets: Rocket[] = [];
    const sparks: Spark[] = [];
    const streamers: Streamer[] = [];

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.round(width * devicePixelRatio);
      canvas.height = Math.round(height * devicePixelRatio);
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    };

    const createRocket = () => {
      const color = randomColor();
      rockets.push({
        x: width * (0.18 + Math.random() * 0.64),
        y: height + 16,
        velocityX: (Math.random() - 0.5) * 0.8,
        velocityY: -(7.8 + Math.random() * 2.4),
        targetY: height * (0.18 + Math.random() * 0.32),
        color,
        trail: [],
      });
    };

    const explodeRocket = (rocket: Rocket) => {
      const sparkCount = 58;
      for (let index = 0; index < sparkCount; index += 1) {
        const angle = (Math.PI * 2 * index) / sparkCount + Math.random() * 0.15;
        const speed = 1.8 + Math.random() * 4.6;
        sparks.push({
          x: rocket.x,
          y: rocket.y,
          velocityX: Math.cos(angle) * speed,
          velocityY: Math.sin(angle) * speed,
          alpha: 1,
          decay: 0.012 + Math.random() * 0.012,
          size: 1.2 + Math.random() * 1.8,
          color: rocket.color,
        });
      }

      const streamerCount = 24;
      for (let index = 0; index < streamerCount; index += 1) {
        streamers.push({
          x: rocket.x,
          y: rocket.y,
          velocityX: (Math.random() - 0.5) * 3.2,
          velocityY: -1.5 + Math.random() * 2,
          length: 18 + Math.random() * 24,
          angle: Math.random() * Math.PI * 2,
          angularVelocity: (Math.random() - 0.5) * 0.16,
          alpha: 1,
          color: randomColor(),
        });
      }
    };

    const draw = (time: number) => {
      const elapsed = time - startTime;
      const delta = Math.min(2, (time - lastTime) / 16.67);
      lastTime = time;
      context.clearRect(0, 0, width, height);

      const launchSchedule = [0, 350, 720, 1_050];
      while (launchIndex < launchSchedule.length && elapsed >= launchSchedule[launchIndex]!) {
        createRocket();
        launchIndex += 1;
      }

      for (let index = rockets.length - 1; index >= 0; index -= 1) {
        const rocket = rockets[index]!;
        rocket.trail.push({ x: rocket.x, y: rocket.y });
        if (rocket.trail.length > 8) rocket.trail.shift();
        rocket.x += rocket.velocityX * delta;
        rocket.y += rocket.velocityY * delta;
        rocket.velocityY += 0.075 * delta;

        context.beginPath();
        rocket.trail.forEach((point, trailIndex) => {
          if (trailIndex === 0) context.moveTo(point.x, point.y);
          else context.lineTo(point.x, point.y);
        });
        context.strokeStyle = colorString(rocket.color, 0.6);
        context.lineWidth = 2;
        context.stroke();
        context.beginPath();
        context.arc(rocket.x, rocket.y, 2.5, 0, Math.PI * 2);
        context.fillStyle = colorString(rocket.color);
        context.fill();

        if (rocket.y <= rocket.targetY || rocket.velocityY >= -0.5) {
          explodeRocket(rocket);
          rockets.splice(index, 1);
        }
      }

      for (let index = sparks.length - 1; index >= 0; index -= 1) {
        const spark = sparks[index]!;
        spark.x += spark.velocityX * delta;
        spark.y += spark.velocityY * delta;
        spark.velocityX *= 0.985;
        spark.velocityY = spark.velocityY * 0.985 + 0.11 * delta;
        spark.alpha -= spark.decay * delta;
        if (spark.alpha <= 0) {
          sparks.splice(index, 1);
          continue;
        }
        context.beginPath();
        context.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2);
        context.fillStyle = colorString(spark.color, spark.alpha);
        context.fill();
      }

      for (let index = streamers.length - 1; index >= 0; index -= 1) {
        const streamer = streamers[index]!;
        streamer.x += streamer.velocityX * delta;
        streamer.y += streamer.velocityY * delta;
        streamer.velocityX *= 0.992;
        streamer.velocityY += 0.11 * delta;
        streamer.angle += streamer.angularVelocity * delta;
        streamer.alpha -= 0.0055 * delta;
        if (streamer.alpha <= 0 || streamer.y > height + streamer.length) {
          streamers.splice(index, 1);
          continue;
        }
        const wave = Math.sin(streamer.angle) * 8;
        context.beginPath();
        context.moveTo(streamer.x, streamer.y);
        context.quadraticCurveTo(
          streamer.x + wave,
          streamer.y + streamer.length / 2,
          streamer.x - wave * 0.55,
          streamer.y + streamer.length,
        );
        context.strokeStyle = colorString(streamer.color, streamer.alpha);
        context.lineWidth = 3;
        context.stroke();
      }

      if (elapsed < CELEBRATION_DURATION_MS || rockets.length > 0 || sparks.length > 0 || streamers.length > 0) {
        frameId = requestAnimationFrame(draw);
      }
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    try {
      if ('vibrate' in navigator) navigator.vibrate([80, 45, 120]);
    } catch {
      // Haptics are optional and unsupported in some embedded browsers.
    }
    const stopSound = playCelebrationSound();
    frameId = requestAnimationFrame(draw);

    return () => {
      resizeObserver.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
      stopSound();
      context.clearRect(0, 0, width, height);
    };
  }, [trigger]);

  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-[15] h-full w-full" />;
}
